import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { ErrorHandler } from "UI/Errors";
import { Event as TEvent } from "../../../../../Global/Base/Events";
import { EnumUtils } from "../../../../../Global/Base/Utils/EnumUtils";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { Control } from "../../../../UI.BaseControls/Control";
import { ChartType } from "../../../../UI.Controls/ChartElement";
import { createElement, registerCustomElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { ChartSettingsContentBuilder } from "../../DataBuilders/ChartSettingsContentBuilder";
import { ChartSettingsHeaderBuilder } from "../../DataBuilders/ChartSettingsHeaderBuilder";
import { IContentBuilder } from "../../DataBuilders/IContentBuilder";
import { ChartSettingsManager } from "./ChartSettingsManager";

const logger = LazyLoadingSectionLogger.create(LoggingSection.chartFrame);

/* eslint-disable jsdoc/check-indentation */
/**
 * panel nastavení grafu
 * sestává ze dvou hlavních částí:
 *      - hlavičky - chartsettingsheaderbuilder = ikony nahoře - typy grafu a tlačítko zavření panelu nastavení grafu
 *      - těla - chartsettingscontentbuilder
 *          - ten sestává ze sekcí:
 *              - svislá osa
 *              - vodorovná osa
 *              - zobrazení
 */
export class ChartSettingsPanel extends Control {
    /** zdálost zavření chartsettingspanelu (klik na křížek vpravo nahoře) */
    public closed: TEvent<ChartSettingsPanel, undefined> = new TEvent<ChartSettingsPanel, undefined>();
    public initializationStarted: boolean = false;

    private _topPanel: HTMLElement;
    private _btnPanel: HTMLElement;
    private _btnClosePanel: HTMLElement;
    private _contentPanel: HTMLElement;
    private _closeBtn: HTMLButtonElement;
    private _align: ChartSettingsPanelAlign;
    private _listenerClick: EventListener;
    private _chartSettingsManager: ChartSettingsManager;
    private _headerBuilder: ChartSettingsHeaderBuilder;
    private _contentBuilder: ChartSettingsContentBuilder;
    private _cssClass: string = "chart-settings-panel";

    public get chartSettingsManager(): ChartSettingsManager {
        return this._chartSettingsManager;
    }

    /**
     * změní-li se ChartSettingsManager, je dle něj taky potřeba upravit zobrazení panelu nastavující graf
     */
    public set chartSettingsManager(v: ChartSettingsManager) {
        this._chartSettingsManager = v;

        if (this._headerBuilder) {
            this._headerBuilder.dispose();
        }
        if (this._contentBuilder) {
            this._contentBuilder.dispose();
        }
        this._headerBuilder = new ChartSettingsHeaderBuilder(this._chartSettingsManager.chartSettings.chartType);
        this._headerBuilder.chartTypeChanged.add((value: ChartType) => this._chartSettingsManager.chartTypeChanged.trigger(value, null));
        this._setContentByBuilder(this._btnPanel, this._headerBuilder);
        this._contentBuilder = new ChartSettingsContentBuilder(this._chartSettingsManager.masterFrame, this._chartSettingsManager.chartSettings.dataSets,
            this._chartSettingsManager.chartSettings.xAxisAttr, this._chartSettingsManager.chartSettings.chartType,
            this._chartSettingsManager.chartSettings.showLegend, this._headerBuilder);
        this._contentBuilder.aggregationFunctionChanged.add(value => this._runChartSettingsManagerEvents(value, this._chartSettingsManager.aggregationFunctionChanged));
        this._contentBuilder.captionChanged.add(value => this._runChartSettingsManagerEvents(value, this._chartSettingsManager.captionChanged));
        this._contentBuilder.attrNameChanged.add(value => this._runChartSettingsManagerEvents(value, this._chartSettingsManager.attrNameChanged));
        this._contentBuilder.deleteDatasetClicked.add(value => this._runChartSettingsManagerEvents(value, this._chartSettingsManager.deleteDatasetClicked));
        this._contentBuilder.xAxisAttrChanged.add(value => this._chartSettingsManager.xAxisAttrChanged.trigger(value, null));
        this._contentBuilder.chartTypeChanged.add((value: ChartType) => this._chartSettingsManager.chartTypeChanged.trigger(value, null));
        this._contentBuilder.chartOrientationChanged.add(value => this._chartSettingsManager.chartOrientationChanged.trigger(value, null));
        this._contentBuilder.showLegendChanged.add(value => this._chartSettingsManager.showLegendChanged.trigger(value, null));
        this._setContentByBuilder(this._contentPanel, this._contentBuilder);
    }

    private _runChartSettingsManagerEvents(value: string[], chartSettingsManagerEvent: TEvent<string[], void>): void {
        chartSettingsManagerEvent.trigger(value, null);
    }

    constructor() {
        super();
        this.classList.add(this._cssClass);
        this._topPanel = createElement({ classes: this._cssClass + "__header" });
        this._btnPanel = createElement({ classes: this._cssClass + "__buttons-panel" });
        this._btnClosePanel = createElement({ type: "button", classes: [this._cssClass + "__close-panel"] });
        Localization.addLocData(this._btnClosePanel, "668877", "title"); // zavřít panel
        this._contentPanel = createElement({ classes: this._cssClass + "__content-panel" });
        this._closeBtn = createElement({ classes: "icon close ui-cross-small" }) as HTMLButtonElement;

        this.appendChild(this._topPanel);
        this.appendChild(this._contentPanel);
        this._topPanel.appendChild(this._btnPanel);
        this._topPanel.appendChild(this._btnClosePanel);
        this._btnClosePanel.appendChild(this._closeBtn);

        this._closeBtn.addEventListener("click", this._listenerClick = () => {
            this.visible = false;

            if (this.closed) {
                this.closed.trigger(this, undefined);
            }

            this.dispose(true);
        });

        this.align = ChartSettingsPanelAlign.right;
    }

    /**
     * zobrazí panel nastavení grafu
     */
    public show(): void {
        //panel mohl být otevřený pro jiný frame
        if (this.visible) {
            this.closed.trigger(this, undefined);
        }

        this.visible = true;
    }

    /**
     * skryje panel nastavení grafu
     */
    public hide(): void {
        this.visible = false;
    }

    public get align(): ChartSettingsPanelAlign | string {
        return this._align;
    }

    public set align(value: ChartSettingsPanelAlign | string) {
        this.classList.remove(ChartSettingsPanelAlign[this._align]);
        if (typeof value === "string") {
            this._align = EnumUtils.getEnumValueFromName(ChartSettingsPanelAlign, value);
        } else {
            this._align = value;
        }
        this.classList.add(ChartSettingsPanelAlign[this._align]);
    }

    public override get visible(): boolean {
        return this.classList.contains("visible");
    }

    public override set visible(v: boolean) {
        this.classList.toggle("visible", v);
    }

    private async _setContentByBuilder(element: HTMLElement, value: IContentBuilder): Promise<void> {
        element.innerHTML = "";
        if (value == null) {
            logger.error("builder not set");
            return;
        }

        try {
            element.appendChild(await value.build());
        } catch (err) {
            logger.error(err);
            ErrorHandler.instance.handleError(err);
        }
    }

    public override dispose(smallDispose: boolean = false): void {
        if (this._headerBuilder) {
            this._headerBuilder.dispose();
        }
        if (this._contentBuilder) {
            this._contentBuilder.dispose();
        }

        if (!smallDispose) {
            this._closeBtn.removeEventListener("click", this._listenerClick);
            super.dispose();
        }
    }
}

export enum ChartSettingsPanelAlign {
    left,
    right,
}

registerCustomElement("tsc-chartsettingspanel", ChartSettingsPanel);
